import { useMemo, useCallback } from 'react';
import { usePaginatedQuery, useQuery } from 'convex/react';
import { api } from '../../api';
import { App } from '@/types/App';
import { deduplicateApps } from '@/utils/appUtils';
import { useEffectiveSerial } from '@/hooks/useEffectiveSerial';
import { useUserDevice } from '@/hooks/useUserDevice';

export const useAvailableApps = () => {
  const { serial, source } = useEffectiveSerial();
  const { device } = useUserDevice();
  
  console.log('useAvailableApps - Serial Resolution:', { serial, source, device: device?._id });
  
  // Determine if we should query (avoid calling backend without a resolved serial)
  const shouldQuery = useMemo(() => !!serial, [serial]);
  
  // Fetch apps using the filter endpoint with pagination
  const { results, status, loadMore } = usePaginatedQuery(
    (api.apps.list as any).allowedByDeviceFilter,
    shouldQuery ? { serial } : "skip",
    { initialNumItems: 50 }
  );
  
  // Fetch statically allowed apps (non-paginated)
  const staticResults = useQuery(
    (api.apps.list as any).allowedByDeviceStatic,
    shouldQuery ? { serial } : "skip"
  );
  
  const isLoading = status === 'LoadingFirstPage' || results === undefined || staticResults === undefined;

  console.log('useAvailableApps - Convex state:', {
    status,
    isLoading,
    filterResultsCount: results?.length,
    staticResultsCount: staticResults?.length,
    hasResults: !!results || !!staticResults
  });

  // Transform and deduplicate data - let Convex handle reactivity
  const data = useMemo(() => {
    if (!results || !Array.isArray(results)) {
      console.log('useAvailableApps - No results or not array:', results);
      return { pages: [] };
    }

    // Combine filter results and static results
    const allResults = [...(results || []), ...(staticResults || [])];
    
    console.log('useAvailableApps - Processing results:', allResults.length);

    const transformedApps: App[] = allResults.map((app: any, index: number) => {
      // Use the id field returned from api.apps.list.availableForDevice
      const convexId = app.id;
      
      if (!convexId) {
        console.error('⚠️ App missing Convex id:', {
          name: app.name || app.trackName,
          trackId: app.trackId,
          rawApp: app
        });
      }
      
      // Use Convex id directly - this should always be present from the API
      const id = convexId 
        ? String(convexId)
        : `temp-${app.trackId || index}`;
      
      const name = app.name || app.trackName || app.title || 'Unknown App';
      const icon = app.iconUrl || app.icon || app.artworkUrl100 || '/placeholder.svg';
      const description = app.description || app.subtitle || '';
      const version = app.version || app.versionString || '';
      const appStoreId = String(app.trackId || '');
      const category = app.category || app.primaryGenreName || app.genre || undefined;
      
      return { id, name, icon, description, version, appStoreId, category };
    })
    // Only require a name to display the app; ensure id is string via fallback above
    .filter(app => !!app.name);
    
    
    const deduplicatedApps = deduplicateApps(transformedApps);
    
    const pages = [{
      apps: deduplicatedApps
    }];
    
    console.log('useAvailableApps - Final data:', {
      totalApps: deduplicatedApps.length,
      sampleApp: deduplicatedApps[0]?.name
    });
    
    return { pages };
  }, [results]);

  // Handle loading and error states
  const error = (!results || results.length === 0) && !isLoading && shouldQuery
    ? new Error('No apps found') 
    : null;

  // No-op functions for pagination compatibility
  const fetchNextPage = useCallback(() => {
    if (status === 'CanLoadMore') {
      loadMore(50);
    }
  }, [loadMore, status]);

  const retry = useCallback(() => {
    console.log('useAvailableApps - Retry not implemented for static endpoint');
  }, []);

  return {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage: status === 'CanLoadMore',
    isFetchingNextPage: status === 'LoadingMore',
    usingDeviceSpecificAPI: shouldQuery,
    retry,
    retryCount: 0
  };
};